// Angular imports
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';

// RxJs imports
import { Subject, Subscription } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { DashboardService } from '../../community-module/_services/data-services/dashboard.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { InformationService } from '../../community-module/_services/data-services/information.service';
import { ScreenService } from '../../_services/shared-services/screen.service';

// Model imports
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';
import { BaseResidentParameterDto } from '../../_models/base-resident-parameter-dto';
import { ExpenseBreakdownResultDto } from '../../community-module/_models/expense-breakdown-result-dto';
import { InformationResultDto } from '../../community-module/_models/information-result-dto';
import { NavigationInfo } from '../../_models/navigation-info';
import { ManagementOfficeData } from './models/management-office.model';
import { ActionItemConfigResultDto } from '../../_models/action-item-config-result-dto';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

/**
 * My Community Tile Component
 *
 * Displays community information on the user dashboard including:
 * - Association Expenses pie chart (Last 12 Months)
 * - Management Office contact information
 * - Links to Community Dashboard and Community Information pages
 */
@Component({
  selector: 'app-my-community-tile',
  templateUrl: './my-community-tile.component.html',
  styleUrls: ['./my-community-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MyCommunityTileComponent implements OnInit, OnDestroy {
  @Input() selectedProperty: UserPropertyResultDto;

  // Loading states
  isLoadingExpenses = true;
  isLoadingOffice = true;
  isLoadingActions = true;

  // Feature visibility flags
  isCommunityDashboardEnabled = false;
  isCommunityInfoEnabled = false;
  showTile = false;

  // Data
  expenseData: ExpenseBreakdownResultDto[] = [];
  managementOffice: ManagementOfficeData | null = null;
  hasExpenseData = false;

  // Responsive
  isSmallScreen = false;

  // Chart configuration
  readonly chartPalette = [
    '#121f38', // Navy
    '#ebe7de', // Sandstone
    '#217655', // Forest Green
    '#f59e0b', // Amber
    '#ff7350', // Tile
    '#4dd5ac', // Mint
    '#ff8f73', // Tile Light
    '#d7e3fe', // Sky
    '#e5563a', // Tile Dark
    '#ebe7de' // Sandstone
  ];
  readonly chartTitle = 'Association Expenses (Last 12 Months)';

  private readonly destroy$ = new Subject<void>();
  private propertyChangeSubscription: Subscription;

  constructor(
    private userService: UserService,
    private dashboardService: DashboardService,
    private helperService: HelperService,
    private informationService: InformationService,
    private screenService: ScreenService,
    private cdr: ChangeDetectorRef
  ) {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }

  ngOnInit(): void {
    this.loadData();

    // Subscribe to property changes
    this.propertyChangeSubscription = RxjsUtils.subscribeToSubject(
      this.userService.getSubjectPropertySelectionChanged(),
      () => {
        this.selectedProperty = this.userService.getSelectedProperty();
        this.loadData();
      }
    );

    // Subscribe to screen size changes
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );
  }

  /**
   * Get legend horizontal alignment based on screen size
   */
  getLegendAlignment(): string {
    return this.isSmallScreen ? 'center' : 'right';
  }

  /**
   * Get legend vertical alignment based on screen size
   */
  getLegendVerticalAlignment(): string {
    return this.isSmallScreen ? 'bottom' : 'middle';
  }

  /**
   * Get chart height based on screen size.
   * Larger on mobile to accommodate legend below the chart.
   */
  getChartHeight(): number {
    return this.isSmallScreen ? 350 : 200;
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.propertyChangeSubscription) {
      this.propertyChangeSubscription.unsubscribe();
    }
  }

  /**
   * Navigate to Community Dashboard page
   */
  onViewCommunityDashboardClick(): void {
    this.navigateToRoute('/community/dashboard');
  }

  /**
   * Navigate to Community Information page
   */
  onViewCommunityInfoClick(): void {
    this.navigateToRoute('/community/information');
  }

  /**
   * Customize pie chart legend text with amount and percentage
   */
  customizeLegendText = (pointInfo: any): string => {
    if (pointInfo.pointName === 'No Data') {
      return pointInfo.pointName;
    }
    const point = this.expenseData[pointInfo.pointIndex];
    if (!point) {
      return pointInfo.pointName;
    }
    const pointName = this.truncateText(pointInfo.pointName, 20);
    const pointValue = this.helperService.getCurrencyStringFormat(
      Math.round(point.balance),
      0
    );
    return `${pointName}\n${pointValue}, ${point.percentage}%`;
  };

  private loadData(): void {
    if (!this.selectedProperty) {
      this.selectedProperty = this.userService.getSelectedProperty();
    }

    if (this.selectedProperty) {
      this.loadUserActions();
    }
  }

  /**
   * Load user action items to determine which sections to display.
   * Checks for 'community-dashboard' and 'community-info' enabled status.
   */
  private loadUserActions(): void {
    this.isLoadingActions = true;
    const param = new BaseResidentParameterDto(
      this.selectedProperty.customerId,
      this.selectedProperty.communityId
    );

    this.userService
      .getUserActionItems(param)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingActions = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (actions: ActionItemConfigResultDto[]) => {
          const dashboardAction = actions?.find(
            (action) => action.item?.toLowerCase() === 'community-dashboard'
          );
          const infoAction = actions?.find(
            (action) => action.item?.toLowerCase() === 'community-info'
          );

          this.isCommunityDashboardEnabled = dashboardAction?.enable ?? false;
          this.isCommunityInfoEnabled = infoAction?.enable ?? false;
          this.showTile =
            this.isCommunityDashboardEnabled || this.isCommunityInfoEnabled;

          // Load data based on enabled features
          if (this.isCommunityDashboardEnabled) {
            this.loadExpenseData();
          } else {
            this.isLoadingExpenses = false;
          }

          if (this.isCommunityInfoEnabled) {
            this.loadManagementOfficeData();
          } else {
            this.isLoadingOffice = false;
          }
        },
        error: () => {
          // On error, hide the tile
          this.isCommunityDashboardEnabled = false;
          this.isCommunityInfoEnabled = false;
          this.showTile = false;
          this.isLoadingExpenses = false;
          this.isLoadingOffice = false;
        }
      });
  }

  private loadExpenseData(): void {
    this.isLoadingExpenses = true;
    const param = new BaseResidentParameterDto(
      this.selectedProperty.customerId,
      this.selectedProperty.communityId
    );

    this.dashboardService
      .getExpenseBreakdown(param)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingExpenses = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (data) => {
          if (data && data.length > 0) {
            this.expenseData = data;
            this.hasExpenseData = true;
          } else {
            this.hasExpenseData = false;
          }
        },
        error: () => {
          this.hasExpenseData = false;
        }
      });
  }

  /**
   * Load Management Office data from community information endpoint.
   */
  private loadManagementOfficeData(): void {
    this.isLoadingOffice = true;
    const param = new BaseResidentParameterDto(
      this.selectedProperty.customerId,
      this.selectedProperty.communityId
    );

    this.informationService
      .getCommunityInformation(param)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingOffice = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (data: InformationResultDto[]) => {
          const officeInfo = data?.find(
            (item) => item.item?.toLowerCase().trim() === 'management office'
          );
          this.managementOffice = officeInfo?.information
            ? this.parseManagementOfficeHtml(officeInfo.information)
            : null;
        },
        error: () => {
          this.managementOffice = null;
        }
      });
  }

  /**
   * Parse HTML content from API to extract structured management office data.
   * Extracts name, address, phone numbers and email addresses using regex patterns.
   * Filters out operating hours and other non-essential text.
   */
  private parseManagementOfficeHtml(html: string): ManagementOfficeData {
    // Strip HTML tags to get plain text for parsing
    let plainText = html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Remove operating hours text
    plainText = plainText.replace(/Operating hours[^.]*\./gi, '');
    plainText = plainText.replace(
      /Hours[:\s]+(?:Monday|Mon|Tuesday|Tue|Wednesday|Wed|Thursday|Thu|Friday|Fri|Saturday|Sat|Sunday|Sun)[^.]*\./gi,
      ''
    );

    // Extract office name - look for text before "office" or location identifiers
    const nameRegex =
      /(?:^|located at(?:\s+the)?(?:\s+[\w-]+)*\s+at\s+)?([A-Z][\w\s]+(?:Office|Clubhouse|Center|Building))/i;
    const nameMatch = plainText.match(nameRegex);
    const name = nameMatch ? nameMatch[1]?.trim() || '' : '';

    // Extract address - look for street address patterns
    // Must start with 1-5 digit street number, followed by street name ending with street suffix
    // Include optional trailing period in match so we can strip it
    const addressRegex =
      /\b\d{1,5}\s+[A-Za-z][A-Za-z\s]+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Drive|Dr|Lane|Ln|Way|Parkway|Pkwy|Circle|Cir|Court|Ct)\.?/gi;
    const addressMatches = plainText.match(addressRegex);
    const addressLines = addressMatches
      ? addressMatches
          .map((addr) => addr.trim().replace(/\.+$/, ''))
          .filter((addr) => addr.length > 0)
          // Filter out false matches containing non-address words
          .filter((addr) => !/\b(email|phone|call|fax|the)\b/i.test(addr))
      : [];

    // Extract all phone numbers
    const phoneRegex = /\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
    const phoneMatches = plainText.match(phoneRegex);
    const phone = phoneMatches?.[0] || '';
    const residentServices = phoneMatches?.[1] || '';

    // Extract email address
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;
    const emailMatches = plainText.match(emailRegex);
    const email = emailMatches ? emailMatches[0] : '';

    return {
      name,
      addressLines,
      phone,
      residentServices,
      email
    };
  }

  private navigateToRoute(route: string): void {
    const info = new NavigationInfo();
    info.customerId = this.selectedProperty?.customerId;
    info.communityId = this.selectedProperty?.communityId?.toString();
    info.navCode = route;
    this.helperService.navigateToRoute(info);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private truncateText(text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  }
}
